<template>
    <VJoyTemplate class="landing-sitemap">
        <template #template-title>
            <h1>{{ $t('sitemap.freelancers.all.locations') }}</h1>
        </template>
        <template #template-subtitle>
            <p>{{ $t('acquisition.banner.subtitle') }}</p>
        </template>
        <template #template-main>
            <SearchBlock page="freelancers_locations" />
            <div class="landing-sitemap__header">
                <h2>{{ $t('sitemap.freelancers.all.locations.top.cities') }}</h2>
            </div>
            <div class="landing-sitemap__grid">
                <div v-for="city in topCities" :key="city.url" class="landing-sitemap__card">
                    <MaltImg :src="`/home-malt/cities/revamp/${city.humanId}`" width="180" height="275" loading="lazy" alt="" />
                    <a :href="$withLocalePrefix(city.url)" class="stretched-link">{{ city.label }}</a>
                </div>
            </div>
            <ul class="landing-sitemap__list">
                <li v-for="entry in locationEntries" :key="entry.url">
                    <VJoyLink v-if="entry.url" :href="$withLocalePrefix(entry.url)">{{ entry.label }}</VJoyLink>
                </li>
            </ul>
        </template>
    </VJoyTemplate>
</template>

<script setup lang="ts">
    import {createError, useAsyncData} from '#app';
    import {SearchBlock, MaltImg} from '#components';
    import {
        useTranslation,
        useSeoMeta,
        useTwitterMetaTags,
        TwitterMetaTags,
        useOpenGraphMetaTags,
        OpenGraphMetaTag,
        useLocale,
        useLocalePrefix,
    } from '#imports';
    import {VJoyTemplate, VJoyLink} from '@maltjoy/core-vue';
    import {Configuration, SitemapApi} from '@api';

    const {t} = useTranslation();

    /* seo */
    const {isMultiLocaleHost} = useLocale();
    const title = t('sitemap.freelancers.all.locations.meta.title');
    const description = t('search.meta.description', ['', '']);
    useSeoMeta({
        ...(isMultiLocaleHost.value && {robots: 'noindex'}),
        title,
        description,
    });
    useTwitterMetaTags(new TwitterMetaTags({card: 'summary', title, description, t}));
    useOpenGraphMetaTags(new OpenGraphMetaTag({title, description, t}));

    /* data */
    const configuration = new Configuration({basePath: ''});
    const sitemapApi = new SitemapApi(configuration);
    const {getLocalePrefix, getLocaleCodeFromPrefix} = useLocalePrefix();
    const localePrefix = getLocalePrefix();
    const {data: locationEntries, error} = await useAsyncData(
        () => sitemapApi.getFreelancerLocations({locale: localePrefix ? getLocaleCodeFromPrefix(localePrefix) : undefined}),
        {deep: true},
    );
    if (error.value) {
        throw createError(error.value);
    }
    const topCities = locationEntries.value?.filter((city) => city.isTopCity);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '~/assets/styles/sitemap.scss';

    $city-card-width: 180px;
    $city-card-height: 275px;

    .landing-sitemap {
        .joy-template-slot-main {
            width: 100%;
        }
        &__header {
            margin-bottom: var(--joy-core-spacing-5);
        }
        &__grid {
            width: 100%;
            display: flex;
            gap: var(--joy-core-spacing-6);
            overflow-x: auto;
            scrollbar-width: none;
            &::-webkit-scrollbar {
                display: none !important;
            }
            @include mq.screen_lg(min) {
                flex-wrap: wrap;
                overflow-x: initial;
            }
        }

        &__card {
            position: relative;
            width: $city-card-width;
            height: $city-card-height;
            border-radius: var(--joy-core-radius-3);
            overflow: hidden;
            text-align: center;
            flex: none;

            > a {
                display: block;
                margin-top: var(--joy-core-spacing-10);
                font-weight: var(--joy-font-weight-bold);
                color: var(--joy-color-neutral-0);
                font-size: var(--joy-font-size-primary-800);
            }
            img {
                position: absolute;
                z-index: -1;
                left: 0;
                width: 100%;
                height: 100%;
                transition: transform 0.75s ease;
            }
            &:hover img {
                transform: scale3d(1.05, 1.05, 1);
            }
        }
    }

    .stretched-link::after {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1;
        content: '';
    }
</style>
